// Admin drawer. Only renders when the signed-in user is in the backend's
// ADMIN_EMAILS allowlist (`useAuth().isAdmin`). Lets you search users by
// email, see their pro flags + Stripe history + any past revocations, and
// grant / revoke the three plan entitlements with an audit-trail reason.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Shield, Search, UserX, UserCheck, X, Loader2 } from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLAN_LABELS = {
  studio: 'Studio Pro',
  freehand: 'Freehand Pro',
  bundle: 'Bundle (both)',
};

export default function AdminDrawer({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  // Debounced lookup. 300 ms is slow enough to avoid hammering /admin/users
  // on every keystroke yet quick enough to feel instant for a 20-row search.
  useEffect(() => {
    if (!open) return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/admin/users`, { params: { q } });
        setResults(data.results || []);
      } catch (err) {
        if (err?.response?.status === 403) {
          toast.error('Admin access denied');
        } else {
          toast.error('Search failed');
        }
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [query, open]);

  const act = useCallback(async (user, plan, action) => {
    const label = PLAN_LABELS[plan] || plan;
    const verb = action === 'grant' ? 'Grant' : 'Revoke';
    const reason = window.prompt(
      `${verb} ${label} for ${user.email}?\n\nType a short reason (saved to the audit log):`,
      action === 'revoke' ? 'refund processed' : '',
    );
    if (reason === null) return; // user cancelled
    setPendingId(`${user.user_id}:${plan}:${action}`);
    try {
      await axios.post(`${API}/admin/entitlement`, {
        user_id: user.user_id,
        plan,
        action,
        reason,
      });
      toast.success(`${verb}ed ${label}`);
      // Refresh the search results so the new state is visible immediately.
      const { data } = await axios.get(`${API}/admin/users`, { params: { q: query.trim() } });
      setResults(data.results || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `${verb} failed`);
    } finally {
      setPendingId(null);
    }
  }, [query]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="admin-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-slate-900" /> Admin
          </SheetTitle>
          <SheetDescription>
            Search users by email and grant or revoke a plan's access. Every
            change is written to the audit log with your email as the actor.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="email or partial email"
            className="pl-9 h-10 rounded-full"
            data-testid="admin-search-input"
            autoFocus
          />
          {searching && (
            <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
          )}
        </div>

        <div className="mt-4 space-y-3">
          {query.trim().length < 2 && (
            <div className="text-xs text-slate-500">Type at least 2 characters to search.</div>
          )}
          {query.trim().length >= 2 && !searching && results.length === 0 && (
            <div className="text-xs text-slate-500" data-testid="admin-no-results">No users match "{query}".</div>
          )}
          {results.map((u) => (
            <UserRow
              key={u.user_id}
              user={u}
              onGrant={(plan) => act(u, plan, 'grant')}
              onRevoke={(plan) => act(u, plan, 'revoke')}
              pendingId={pendingId}
            />
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function UserRow({ user, onGrant, onRevoke, pendingId }) {
  const studioPro = !!user.is_pro;
  const freehandPro = !!user.is_freehand_pro;
  const txns = user.transactions || [];
  const revokes = user.revocations || [];
  const key = (plan, action) => `${user.user_id}:${plan}:${action}`;
  const busy = (plan, action) => pendingId === key(plan, action);

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-3 space-y-2"
      data-testid={`admin-user-${user.email}`}
    >
      <div className="flex items-center gap-3">
        {user.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-slate-200" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-slate-900 truncate">{user.email}</div>
          <div className="text-[11px] text-slate-500 truncate">{user.name || user.user_id}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 text-[11px]">
        <PlanBadge active={studioPro} label="Studio Pro" />
        <PlanBadge active={freehandPro} label="Freehand Pro" />
      </div>

      <div className="grid grid-cols-2 gap-1.5 pt-1">
        <AdminActionButton
          testId={`admin-${user.user_id}-studio-${studioPro ? 'revoke' : 'grant'}`}
          disabled={busy('studio', studioPro ? 'revoke' : 'grant')}
          onClick={() => (studioPro ? onRevoke('studio') : onGrant('studio'))}
          revoke={studioPro}
          label="Studio Pro"
        />
        <AdminActionButton
          testId={`admin-${user.user_id}-freehand-${freehandPro ? 'revoke' : 'grant'}`}
          disabled={busy('freehand', freehandPro ? 'revoke' : 'grant')}
          onClick={() => (freehandPro ? onRevoke('freehand') : onGrant('freehand'))}
          revoke={freehandPro}
          label="Freehand Pro"
        />
        <AdminActionButton
          testId={`admin-${user.user_id}-bundle-grant`}
          disabled={busy('bundle', 'grant') || (studioPro && freehandPro)}
          onClick={() => onGrant('bundle')}
          revoke={false}
          label="Bundle"
        />
        <AdminActionButton
          testId={`admin-${user.user_id}-bundle-revoke`}
          disabled={busy('bundle', 'revoke') || (!studioPro && !freehandPro)}
          onClick={() => onRevoke('bundle')}
          revoke={true}
          label="Bundle"
        />
      </div>

      {txns.length > 0 && (
        <details className="mt-1">
          <summary className="text-[11px] text-slate-500 cursor-pointer">
            Transactions ({txns.length})
          </summary>
          <div className="mt-1.5 space-y-1">
            {txns.map((t) => (
              <div
                key={t.session_id || t.payment_intent_id || `${t.plan}-${t.created_at}`}
                className="text-[11px] text-slate-600 flex items-center justify-between gap-2"
              >
                <span className="truncate">
                  {PLAN_LABELS[t.plan] || t.plan} — ${Number(t.amount || 0).toFixed(2)}
                </span>
                <span className={`shrink-0 font-mono text-[10px] px-1.5 py-0.5 rounded ${
                  t.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700'
                  : t.payment_status === 'refunded' ? 'bg-rose-50 text-rose-700'
                  : t.payment_status === 'disputed' ? 'bg-amber-50 text-amber-700'
                  : 'bg-slate-50 text-slate-500'
                }`}>
                  {t.payment_status || t.status}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      {revokes.length > 0 && (
        <details>
          <summary className="text-[11px] text-slate-500 cursor-pointer">
            Audit log ({revokes.length})
          </summary>
          <div className="mt-1.5 space-y-1">
            {revokes.map((r) => (
              <div
                key={`${r.revoked_at}-${r.plan}-${r.action || 'revoke'}`}
                className="text-[11px] text-slate-500 leading-snug"
              >
                <span className="font-mono text-[10px] text-slate-400">{new Date(r.revoked_at).toLocaleString()}</span>
                {' · '}
                <span className="text-slate-700">{r.action || 'revoke'}</span>
                {' '}
                <span>{PLAN_LABELS[r.plan] || r.plan}</span>
                {r.reason ? <> — {r.reason}</> : null}
                {r.actor_email ? <> <span className="text-slate-400">by {r.actor_email}</span></> : null}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function PlanBadge({ active, label }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${
      active ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-500'
    }`}>
      {active ? <UserCheck className="w-3 h-3" /> : <X className="w-3 h-3" />}
      {label}
    </span>
  );
}

function AdminActionButton({ onClick, revoke, label, disabled, testId }) {
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={`h-8 rounded-lg text-[11px] font-medium ${
        revoke
          ? 'border-rose-200 text-rose-700 hover:bg-rose-50 hover:border-rose-300'
          : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300'
      }`}
    >
      {revoke ? <UserX className="w-3 h-3 mr-1" /> : <UserCheck className="w-3 h-3 mr-1" />}
      {revoke ? `Revoke ${label}` : `Grant ${label}`}
    </Button>
  );
}
